import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router'

import type { Route } from './+types/root'
import './app.css'
import { CookieConsentProvider } from './components/cookie-consent/cookie-consent-provider'
import { Toaster } from './components/ui'
import { Container, VStack, styled } from './styled-system/jsx'

export const links: Route.LinksFunction = () => [
    // Favicons and web app manifest
    { rel: 'icon', href: '/favicon.ico', sizes: '32x32' },
    { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
    { rel: 'icon', href: '/favicon-96x96.png', type: 'image/png', sizes: '96x96' },
    { rel: 'manifest', href: '/manifest.json' },
]

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang="en" data-theme="dark">
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta name="theme-color" content="#2B2B36" />
                <Meta />
                <Links />
            </head>
            <body>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    )
}

export default function App() {
    return (
        <CookieConsentProvider>
            <Outlet />
            <Toaster />
        </CookieConsentProvider>
    )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    let message = 'Oops!'
    let details = 'An unexpected error occurred.'
    let stack: string | undefined

    if (isRouteErrorResponse(error)) {
        message = error.status === 404 ? '404' : 'Error'
        details = error.status === 404 ? 'The requested page could not be found.' : error.data || error.statusText || details
    } else if (error instanceof Error) {
        // Always expose detailed error information in development
        if (import.meta.env.DEV) {
            details = error.message
            stack = error.stack
        }
        // In production, keep the generic message
    }

    return (
        <html lang="en" data-theme="dark">
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <Meta />
                <Links />
            </head>
            <body>
                <Container maxW="4xl" px={{ base: '4', md: '6' }} py={{ base: '16', md: '24' }}>
                    <VStack alignItems="start" gap="5">
                        <styled.h1 fontSize={{ base: '3xl', md: '5xl' }} fontWeight="bold" color="red.400">
                            {message}
                        </styled.h1>
                        <styled.p fontSize="lg" color="fg.muted">{details}</styled.p>
                    {stack && (
                        <styled.pre
                            w="full"
                            p="4"
                            overflowX="auto"
                            bg="brand.raisinBlack"
                            borderRadius="md"
                            fontSize="sm"
                            borderWidth="1px"
                            borderColor="border.subtle"
                        >
                            <code>{stack}</code>
                        </styled.pre>
                    )}
                        <styled.a
                            href="/dashboard"
                            display="inline-flex"
                            alignItems="center"
                            px="5"
                            py="3"
                            bg="accent.default"
                            color="brand.raisinBlack"
                            borderRadius="md"
                            textDecoration="none"
                            fontWeight="semibold"
                            _hover={{ bg: 'accent.subtle' }}
                        >
                            Go to dashboard
                        </styled.a>
                    </VStack>
                </Container>
                <Scripts />
            </body>
        </html>
    )
}
